// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the error budget a release descriptor carries, read and put in
 * words (docs/AYNI_DATA_INTAKE_PLAN.md, "Transparency about errors and
 * tolerances"; the fields are docs/AYNI_DATA_INTAKE_API.md §10.1;
 * decision O9, 2026-10-06: every release descriptor carries them, public
 * streams too). Used by the job page's review, the info panel's
 * Accuracy section, and the ± on every value read from the video
 * (pointer readout, pins, Analyze).
 *
 * **The ± on a value read from the video** is half an 8-bit step plus the
 * codec's 99th percentile. The codec figure is measured against what went
 * into the encoder (the 8-bit codes), and rounding to a code is never
 * further than half a step from the field, so for every cell
 * |rounding + codec| ≤ half-step + |codec|: at least 99 % of cells are
 * within the sum. It leaves out regridding and clipping, which the
 * Accuracy section lists on their own. Without a budget the app shows the
 * 8-bit step only, as before.
 *
 * **Rounding** follows the usual rule for stating an uncertainty: one
 * significant figure, two when it begins with a 1, and the value rounded
 * to the same decimal place (J. R. Taylor, *An Introduction to Error
 * Analysis*, 2nd ed., 1997, §2.2 and §2.5).
 */

import { t, type MessageKey } from '../i18n'
import { formatNumber } from '../i18n/format'
import type { ErrorBudget, ReleaseAccuracy, ToleranceResult } from '../types/error-budget'

export type { ErrorBudget, ReleaseAccuracy, ToleranceResult } from '../types/error-budget'
export { parseErrorBudget, parseReleaseAccuracy, parseTolerance } from '../types/error-budget'

/** Whether two budgets say different things (after reading: unknown fields and order do not count). */
export function budgetsDiffer(a: ErrorBudget | null, b: ErrorBudget | null): boolean {
  if (!a || !b) return false
  return JSON.stringify(a) !== JSON.stringify(b)
}

/**
 * The ± for a value read from the video: half a step plus the codec's
 * 99th percentile (see the module note). `step` is the encoding's own
 * step, used when the budget does not state one. Null without a measured
 * codec error: the caller then shows the 8-bit step only.
 */
export function videoUncertainty(acc: ReleaseAccuracy | null | undefined, step: number | null): number | null {
  const p99 = acc?.budget.codec?.p99
  if (p99 === null || p99 === undefined || p99 < 0) return null
  const half = acc!.budget.quantisation?.halfStep ?? (step !== null && step > 0 ? step / 2 : null)
  if (half === null) return null
  const u = half + p99
  return u > 0 ? u : null
}

/**
 * Round an uncertainty to one significant figure (two when it starts
 * with a 1), and say how many decimals the value should then carry.
 * `decimals` may be negative (round to tens, hundreds).
 */
export function roundUncertainty(u: number): { u: number; decimals: number } {
  if (!(u > 0) || !Number.isFinite(u)) return { u: 0, decimals: 0 }
  let exp = Math.floor(Math.log10(u))
  let lead = u / 10 ** exp
  // Float noise: 0.1 can come out as 0.0999…, which is a leading 9.
  if (lead >= 9.9999999) {
    exp += 1
    lead = 1
  }
  const figures = lead < 2 ? 2 : 1
  const decimals = figures - 1 - exp
  const factor = 10 ** decimals
  let rounded = Math.round(u * factor) / factor
  // One figure that rounds up to the next decade (0.96 → 1) gains a figure: keep one.
  if (figures === 1 && rounded >= 10 ** (exp + 1)) return roundUncertainty(rounded)
  rounded = Number(rounded.toFixed(Math.max(0, decimals)))
  return { u: rounded, decimals }
}

/** A value rounded to the decimal place its uncertainty supports. */
export function roundToUncertainty(value: number, u: number): { value: number; u: number; decimals: number } {
  const r = roundUncertainty(u)
  const factor = 10 ** r.decimals
  const v = Math.round(value * factor) / factor
  return { value: Number(v.toFixed(Math.max(0, r.decimals))) || 0, u: r.u, decimals: r.decimals }
}

/** "12.3 ± 0.2 m s-1": a value and its uncertainty, rounded together. */
export function formatWithUncertainty(value: number, u: number, units?: string | null): string {
  const r = roundToUncertainty(value, u)
  const digits = Math.max(0, r.decimals)
  const opts: Intl.NumberFormatOptions = { minimumFractionDigits: digits, maximumFractionDigits: digits }
  const v = formatNumber(r.value, opts)
  const e = formatNumber(r.u, opts)
  return units ? t('probe.valueWithError', { value: v, error: e, units }) : t('probe.valueWithErrorNoUnits', { value: v, error: e })
}

// ------------------------------------------------------------ in words

const NUM = { maximumSignificantDigits: 3 } as const

function amount(v: number | null, units: string | null): string {
  if (v === null) return t('accuracy.notMeasured')
  const text = formatNumber(v, NUM)
  return units ? t('probe.value', { value: text, units }) : text
}

function share(v: number | null): string {
  if (v === null) return t('accuracy.notMeasured')
  return formatNumber(v, { style: 'percent', maximumSignificantDigits: 2 })
}

function count(v: number | null): string {
  return v === null ? t('accuracy.notMeasured') : formatNumber(v)
}

const REGRID_NAMES: Readonly<Record<string, MessageKey>> = {
  nearest: 'myData.regrid.nearest',
  bilinear: 'myData.regrid.bilinear',
  conservative: 'myData.regrid.conservative',
}

/** A regridding method's name for people (an unknown one as published). */
export function regridName(method: string | null): string {
  if (!method) return t('accuracy.notMeasured')
  const key = REGRID_NAMES[method]
  return key ? t(key) : method
}

export interface BudgetRow {
  readonly key: 'quantisation' | 'codec' | 'clipping' | 'regrid' | 'time' | 'noData'
  readonly label: string
  readonly text: string
}

/**
 * The budget as rows of plain words, one per error source, in the plan's
 * order. A source the budget leaves out is left out. `units` is used when
 * the budget does not state its own.
 */
export function budgetRows(budget: ErrorBudget, units: string | null = null): BudgetRow[] {
  const u = budget.units ?? units
  const rows: BudgetRow[] = []
  const q = budget.quantisation
  if (q) rows.push({ key: 'quantisation', label: t('accuracy.row.quantisation'), text: t('accuracy.quantisation', { step: amount(q.step, u), half: amount(q.halfStep, u) }) })
  const c = budget.codec
  if (c) {
    const worst = c.worstFrame
    const frame = worst ? (worst.time ?? (worst.index !== null ? t('accuracy.frameNumber', { n: formatNumber(worst.index) }) : null)) : null
    const base = t('accuracy.codec', { mean: amount(c.mean, u), p99: amount(c.p99, u), max: amount(c.max, u) })
    rows.push({ key: 'codec', label: t('accuracy.row.codec'), text: frame ? `${base} ${t('accuracy.worstFrame', { frame })}` : base })
  }
  const cl = budget.clipping
  if (cl) rows.push({ key: 'clipping', label: t('accuracy.row.clipping'), text: t('accuracy.clipping', { low: share(cl.lowShare), high: share(cl.highShare), furthest: amount(cl.furthest, u) }) })
  const rg = budget.regrid
  if (rg) {
    rows.push({
      key: 'regrid',
      label: t('accuracy.row.regrid'),
      text: t('accuracy.regrid', {
        method: regridName(rg.method),
        from: rg.sourceResolution ?? t('accuracy.notMeasured'),
        to: rg.targetResolution ?? t('accuracy.notMeasured'),
        mean: amount(rg.mean, u),
        p99: amount(rg.p99, u),
      }),
    })
  }
  const tm = budget.time
  if (tm) rows.push({ key: 'time', label: t('accuracy.row.time'), text: t('accuracy.time', { missing: count(tm.missing), repeated: count(tm.repeated), frozen: count(tm.frozen) }) })
  const nd = budget.noData
  if (nd) rows.push({ key: 'noData', label: t('accuracy.row.noData'), text: t('accuracy.noData', { mean: share(nd.meanShare), max: share(nd.maxShare) }) })
  return rows
}

/** "99 % of values within 0.1 m s-1". */
export function toleranceText(tol: { quantile: number; abs: number }, units: string | null): string {
  return t('accuracy.toleranceRule', {
    quantile: formatNumber(tol.quantile, { style: 'percent', maximumSignificantDigits: 4 }),
    abs: amount(tol.abs, units),
  })
}

/** The sentence under the table: does the release meet the tolerance the customer set? */
export function toleranceVerdict(tol: ToleranceResult | { quantile: number; abs: number } | null, pass: boolean | null, units: string | null): string {
  if (!tol) return t('accuracy.tolerance.none')
  const rule = toleranceText(tol, units)
  const achieved = 'achieved' in tol && tol.achieved !== null ? amount(tol.achieved, units) : null
  if (pass === true) return achieved ? t('accuracy.tolerance.passAchieved', { rule, achieved }) : t('accuracy.tolerance.pass', { rule })
  if (pass === false) return achieved ? t('accuracy.tolerance.failAchieved', { rule, achieved }) : t('accuracy.tolerance.fail', { rule })
  return t('accuracy.tolerance.unknown', { rule })
}
